import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";

export default function Scenarios() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";

  const scenarios = useMemo(
    () => [
      {
        tag: t("scenarios.item1.tag"),
        title: t("scenarios.item1.title"),
        description: t("scenarios.item1.desc"),
        tags: t("scenarios.item1.tags").split(","),
        image:
          "/images/misc/scenario_1.jpg",
      },
      {
        tag: t("scenarios.item2.tag"),
        title: t("scenarios.item2.title"),
        description: t("scenarios.item2.desc"),
        tags: t("scenarios.item2.tags").split(","),
        image:
          "/images/misc/scenario_2.jpg",
      },
      {
        tag: t("scenarios.item3.tag"),
        title: t("scenarios.item3.title"),
        description: t("scenarios.item3.desc"),
        tags: t("scenarios.item3.tags").split(","),
        image:
          "/images/misc/scenario_3.jpg",
      },
    ],
    [t]
  );

  return (
    <section
      id="scenarios"
      className={`relative py-24 sm:py-32 ${
        isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"
      }`}
    >
      {/* Section Title */}
      <div className="relative mx-auto max-w-7xl px-6 sm:px-10 mb-16 sm:mb-24">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span
              className={`inline-block text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight select-none whitespace-nowrap ${
                isLight ? "text-[#1c1c1e]/[0.04]" : "text-foreground/[0.04]"
              }`}
            >
              {t("scenarios.bg_text")}
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {t("scenarios.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`mt-5 text-base md:text-lg ${
              isLight ? "text-[#6e6e73]" : "text-silver-400"
            }`}
          >
            {t("scenarios.subtitle")}
          </motion.p>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0">
          {scenarios.map((item, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`group relative overflow-hidden rounded-2xl md:rounded-none ${
                index > 0
                  ? isLight
                    ? "md:border-l md:border-black/10"
                    : "md:border-l md:border-white/10"
                  : ""
              }`}
            >
              {/* Image */}
              <div className="relative h-56 sm:h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.tag}
                  className="w-full h-full object-cover object-top opacity-60 group-hover:opacity-80 transition-opacity duration-700"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-t ${
                    isLight
                      ? "from-[#f5f5f7] via-[#f5f5f7]/50 to-transparent"
                      : "from-cosmic-950 via-cosmic-950/50 to-transparent"
                  }`}
                />
              </div>

              {/* Content */}
              <div className="p-7 sm:p-8">
                <span
                  className={`text-xs font-semibold uppercase tracking-[0.15em] ${
                    isLight ? "text-[#2d2d2d]" : "text-gold-500"
                  }`}
                >
                  {item.tag}
                </span>
                <h3
                  className={`mt-3 mb-4 text-xl font-semibold leading-[1.38] tracking-tight md:text-2xl whitespace-pre-line ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {item.title}
                </h3>
                <p
                  className={`text-sm font-light leading-7 ${
                    isLight ? "text-[#6e6e73]" : "text-silver-500"
                  }`}
                >
                  {item.description}
                </p>
                <div
                  className={`mt-8 flex flex-wrap items-center gap-2 text-xs ${
                    isLight ? "text-[#6e6e73]" : "text-silver-500"
                  }`}
                >
                  {item.tags.map((tag, i) => (
                    <span key={i} className="flex items-center">
                      {i > 0 && (
                        <span
                          className={`mr-2 ${
                            isLight ? "text-[#6e6e73]/50" : "text-silver-500/50"
                          }`}
                        >
                          ·
                        </span>
                      )}
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}